'use client';

/**
 * Widget share launcher — the HOST-side mount behind `host.requestShare`.
 *
 * Security model: a sandboxed widget can ASK to share its own project, but it
 * can NEVER change access control. This component (trusted portal chrome,
 * rendered OVER the canvas, never inside the widget iframe) listens for the
 * `widget-request-share` CustomEvent the bridge dispatches and opens the system
 * {@link ShareDialog} targeted at the widget's project content id. The dialog
 * itself loads its grant summary via the grant route, which answers a
 * non-owner with a limited `canManage: false` summary (`assertActorCanManage`
 * gates the full one), so a recipient who requests a share sees the dialog in
 * read-only mode — copy-link only; grants can only be applied by the human
 * owner clicking Save. The widget never sees or controls the outcome.
 *
 * Mounted once per workspace shell (like {@link WidgetContentPicker}).
 */

import { useEffect, useState } from 'react';
import { ShareDialog, type ShareLinkTypeSpec } from '@/components/sharing/share-dialog';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';

interface ShareRequest {
  contentIds: string[];
  linkTypes?: ShareLinkTypeSpec[];
}

export function WidgetShareLauncher() {
  const [request, setRequest] = useState<ShareRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!request);

  useEffect(() => {
    function onRequest(e: Event) {
      // Accept either a single `contentId` (legacy) or a `contentIds` array (a
      // file browser sharing a multi-selection). The bridge already confined
      // the ids to what the widget was served; the dialog re-gates ownership.
      const detail = (e as CustomEvent).detail as
        | { contentId?: unknown; contentIds?: unknown; linkTypes?: unknown }
        | undefined;
      const list = Array.isArray(detail?.contentIds)
        ? detail!.contentIds
        : detail?.contentId != null
          ? [detail.contentId]
          : [];
      const contentIds = list.filter((x): x is string => typeof x === 'string' && x.length > 0);
      // App-declared typed links (already validated bridge-side); re-filter
      // defensively since anyone can dispatch a CustomEvent.
      const linkTypes = Array.isArray(detail?.linkTypes)
        ? (detail!.linkTypes as unknown[])
            .filter(
              (t): t is ShareLinkTypeSpec =>
                !!t &&
                typeof t === 'object' &&
                typeof (t as ShareLinkTypeSpec).type === 'string' &&
                /^[a-z][a-z0-9-]{0,31}$/.test((t as ShareLinkTypeSpec).type) &&
                typeof (t as ShareLinkTypeSpec).label === 'string',
            )
            .slice(0, 4)
        : undefined;
      if (contentIds.length) {
        captureAppFocus();
        setRequest({ contentIds, linkTypes });
      }
    }
    window.addEventListener('widget-request-share', onRequest as EventListener);
    return () => window.removeEventListener('widget-request-share', onRequest as EventListener);
  }, [captureAppFocus]);

  if (!request) return null;
  return (
    <ShareDialog
      kind="content"
      contentIds={request.contentIds}
      linkTypes={request.linkTypes}
      onClose={() => setRequest(null)}
    />
  );
}
